import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Sparkles } from "lucide-react";

const Ctx = createContext(null);

// Pre-computed sparkle particles radiating from the center.
const SPARKLES = Array.from({ length: 16 }).map((_, i) => {
  const angle = (i / 16) * Math.PI * 2;
  const dist = 120 + (i % 4) * 46;
  return {
    id: i,
    dx: Math.cos(angle) * dist,
    dy: Math.sin(angle) * dist,
    delay: (i % 6) * 40,
    size: 10 + (i % 3) * 6,
  };
});

export function CartTransitionProvider({ children }) {
  const nav = useNavigate();
  const [active, setActive] = useState(false);
  const busy = useRef(false);

  const triggerCartTransition = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setActive(true);
    // Navigate partway through so the cart is already mounting behind the veil.
    setTimeout(() => nav("/cart"), 620);
    setTimeout(() => {
      setActive(false);
      busy.current = false;
    }, 1050);
  }, [nav]);

  return (
    <Ctx.Provider value={{ triggerCartTransition }}>
      {children}
      {active && (
        <div className="cart-magic-overlay" data-testid="cart-magic-transition" aria-hidden>
          <div className="cart-magic-portal" />
          <div className="cart-magic-ring" />
          <div className="cart-magic-bag">
            <ShoppingBag size={46} strokeWidth={2.2} />
          </div>
          {SPARKLES.map((s) => (
            <span
              key={s.id}
              className="cart-magic-spark"
              style={{
                "--dx": `${s.dx}px`,
                "--dy": `${s.dy}px`,
                animationDelay: `${s.delay}ms`,
              }}
            >
              <Sparkles size={s.size} />
            </span>
          ))}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useCartTransition() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCartTransition must be used within CartTransitionProvider");
  return ctx;
}
